import { useCallback } from "react"
import { useLocation, useSearchParams } from "react-router"

import {
  stageSearchParamUpdates,
  useSearchParamStagingScope,
} from "@/utils/url/url-utils"

interface SetSearchParamsPageOptions {
  replace?: boolean
}

interface UseSearchParamsPageValue {
  isPageInSearchParams: boolean

  searchParamsPage: number | undefined

  setSearchParamsPage: (
    year: number,
    options?: SetSearchParamsPageOptions
  ) => void
}

/**
 * Manages the page value in the search params.
 *
 * @example
 * const {
 *   isPageInSearchParams,
 *   searchParamsPage
 *   setSearchParamsPage
 * } = useSearchParamsPage()
 */
export const useSearchParamsPage = (): UseSearchParamsPageValue => {
  const location = useLocation()
  const searchParamStagingScope = useSearchParamStagingScope()
  const [searchParams, setSearchParams] = useSearchParams()

  const searchParamsPage: number | undefined =
    Number.parseInt(searchParams.get("page") ?? "") || undefined

  const isPageInSearchParams = Boolean(searchParamsPage)

  const setSearchParamsPage = useCallback(
    (page: number, options: SetSearchParamsPageOptions = {}) => {
      const { replace = false } = options

      stageSearchParamUpdates({
        committed: searchParams,
        locationKey: location.key,
        replace,
        scope: searchParamStagingScope,
        setSearchParams,
        updates: { page },
      })
    },
    [location.key, searchParamStagingScope, searchParams, setSearchParams]
  )

  return {
    isPageInSearchParams,
    searchParamsPage,
    setSearchParamsPage,
  }
}
